/// <amd-module name="Agile/Directory/Redux/DirectorySaga" />
import { ExceptionInfo } from "Agile/Contracts/Common";
import { DirectoryConstants, DirectoryGroupKeys } from "Agile/Directory/DirectoryConstants";
import { DirectoryActions, DirectoryActionTypes } from "Agile/Directory/Redux/DirectoryActions";
import { getFavoriteStatus, getGroupCollapsedMap, isAllSectionLoading, isAllSectionPopulated } from "Agile/Directory/Redux/DirectorySelectors";
import { FavoriteActions, FavoriteActionTypes } from "Agile/Directory/Redux/Favorites/FavoriteActions";
import { FavoriteStatus, IDirectoryArtifact, IDirectoryContext, IDirectoryDataProviderData, IMyDirectoryDataProviderData } from "DirectoryContracts";
import { Favorite, FavoriteCreateParameters } from "Favorites/Common/Contracts/Favorite";
import { IFavoriteRestClient } from "Favorites/Common/RestClient/Favorite";
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType, ActionWithPayload } from "redux/Utils/ActionHelper";
import { IProjectData } from "Tfs/Platform/Page";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataSelectors";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { traceError, traceException } from "VSS/Platform/Trace";
import { HubViewStateActionTypes } from "VSS/Redux/Deprecated/HubViewState";

/**
 * Root level saga for all directory pages
 */
export function* directorySaga(): SagaIterator {
    // Register sagas to load all data in certain scenarios
    yield effects.takeEvery(DirectoryActionTypes.RowGroupToggled, groupToggled);
    yield effects.takeEvery(HubViewStateActionTypes.FilterStateChanged, loadAllDataFromDataProvider);

    // Register saga to listen to favorite actions
    yield effects.takeEvery(FavoriteActionTypes.ToggleFavorite, toggleFavorite);

    // Load the group mapping
    yield effects.call(loadSavedRowGroupCollapsedMapping);

    // Load the initial data
    yield effects.call(loadInitialDataFromDataProvider);
}

/**
 * Saga which loads the initial data from the data provider
 */
export function* loadInitialDataFromDataProvider<T extends IDirectoryArtifact>(): SagaIterator {
    const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;

    const directoryData: IMyDirectoryDataProviderData<T> = (yield effects.call(
        [contributionService, contributionService.getData],
        directoryContext.myDataProviderId
    )) as any;

    const error = _verifyAndReportValidData(directoryData);
    if (!error) {
        yield effects.put(DirectoryActions.initialDataLoaded(directoryData));

        // If there were no artifacts, expand the all group and load the items initially
        if (
            (!directoryData.memberKeys || directoryData.memberKeys.length === 0) &&
            (!directoryData.favorites || directoryData.favorites.length === 0)
        ) {
            yield effects.put(DirectoryActions.rowGroupToggled(DirectoryGroupKeys.AllGroupKey, false));
        }
    } else {
        const { message, exceptionInfo } = error;
        yield effects.put(DirectoryActions.initialDataLoadFailed(exceptionInfo));

        yield effects.call(traceError, pageContext, directoryContext.hubName, "Directory", message);
    }
}

export function* loadSavedRowGroupCollapsedMapping(): SagaIterator {
    try {
        const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;
        const key = getLocalStorageKey(directoryContext.hubName);
        const cachedStateJson: string | null = (yield effects.call([localStorage, localStorage.getItem], key)) as any;
        if (cachedStateJson) {
            const cachedState: { [key: string]: boolean } = JSON.parse(cachedStateJson);
            yield effects.put(DirectoryActions.rowGroupCollapsedInitialized(cachedState));
        }
    } catch {
        // no-op on error, use the default state
    }
}

export function* groupToggled(action: ActionsOfType<DirectoryActions, DirectoryActionTypes.RowGroupToggled>): SagaIterator {
    const { rowGroupKey, collapsed } = action.payload;
    if (rowGroupKey === DirectoryGroupKeys.AllGroupKey && !collapsed) {
        yield effects.call(loadAllDataFromDataProvider);
    }

    try {
        const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;

        // Save the group state to local storage
        const groupState = (yield effects.select(getGroupCollapsedMap)) as any;
        const serializedGroupState = JSON.stringify(groupState);
        const key = getLocalStorageKey(directoryContext.hubName);
        yield effects.call([localStorage, localStorage.setItem], key, serializedGroupState);
    } catch {
        // no -op
    }
}

/**
 * Saga which loads the all data from the data provider
 * If the group toggled is the ALL group, and the all group has never been expanded, this saga will execute
 * @param action The group toggled action
 */
export function* loadAllDataFromDataProvider<T extends IDirectoryArtifact>(): SagaIterator {
    const allSectionLoading: boolean = (yield effects.select(isAllSectionLoading)) as any;
    const allSectionPopulated: boolean = (yield effects.select(isAllSectionPopulated)) as any;

    if (!allSectionLoading && !allSectionPopulated) {
        yield effects.put(DirectoryActions.beginLoadAllData());
        const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const contributionService: IVssContributionService = (yield effects.call(
            [pageContext, pageContext.getService],
            "IVssContributionService"
        )) as any;

        const directoryData: IDirectoryDataProviderData<T> = (yield effects.call(
            [contributionService, contributionService.getDataAsync],
            directoryContext.allDataProviderId
        )) as any;

        const error = _verifyAndReportValidData(directoryData);
        if (!error) {
            yield effects.put(DirectoryActions.loadAllDataSucceeded(directoryData));
        } else {
            const { message, exceptionInfo } = error;
            yield effects.put(DirectoryActions.loadAllDataFailed(exceptionInfo));

            yield effects.call(traceError, pageContext, directoryContext.hubName, "Directory", message);
        }
    }
}

/**
 * Function which verifies payload from the data provider is well formed
 * @param pageData The data provider data
 * @returns An error payload if the data is invalid, undefined if the data is valid
 */
function _verifyAndReportValidData<T extends IDirectoryArtifact>(
    pageData: IDirectoryDataProviderData<T>
): { message: string; exceptionInfo: ExceptionInfo } | undefined {
    if (!pageData) {
        return {
            message: "The data provider didn't return any data",
            exceptionInfo: { exceptionMessage: "Server returned no response" }
        };
    } else if (pageData.exceptionInfo) {
        return {
            message: `The data provider contained exception info: ${JSON.stringify(pageData.exceptionInfo)}`,
            exceptionInfo: pageData.exceptionInfo
        };
    } else if (!pageData.artifacts) {
        return {
            message: "The data provider returned invalid data. Teams is null",
            exceptionInfo: {
                exceptionMessage: "The server returned an invalid response"
            }
        };
    }
}

/**
 * Saga which is executed when the user clicks the favorite star
 * Determines which favorite serivce function to call (favorite/unfavorite)
 * @param action The toggle favorite action
 */
export function* toggleFavorite(
    action: ActionWithPayload<
        FavoriteActionTypes.ToggleFavorite,
        {
            artifactId: string;
            artifactName: string;
            favoriteType: string;
            favoriteId?: string;
        }
    >
): SagaIterator {
    const { artifactId, artifactName, favoriteType, favoriteId } = action.payload;
    const currentFavoritingState = (yield effects.select(getFavoriteStatus(artifactId))) as any;

    if (currentFavoritingState === FavoriteStatus.Favoriting || currentFavoritingState === FavoriteStatus.Unfavoriting) {
        return;
    } else if (!currentFavoritingState || currentFavoritingState === FavoriteStatus.Unfavorited) {
        yield effects.call(createFavorite, artifactId, artifactName, favoriteType);
    } else if (favoriteId) {
        yield effects.call(deleteFavorite, artifactId, favoriteId, favoriteType);
    }
}

/**
 * Saga which creates a new favorite
 * @param artifactId The artifact ID of the new favorite
 * @param artifactName The name of the artifact
 */
export function* createFavorite(artifactId: string, artifactName: string, favoriteType: string): SagaIterator {
    yield effects.put(FavoriteActions.beginAddFavorite(artifactId));
    const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    try {
        const favoritesClient: IFavoriteRestClient = (yield effects.call([pageContext, pageContext.getRestClient], "IFavoriteRestClient")) as any;
        const project: IProjectData = (yield effects.select(currentProject)) as any;

        const favoriteParameters: FavoriteCreateParameters = {
            artifactId,
            artifactProperties: {},
            artifactName,
            artifactType: favoriteType,
            artifactScope: {
                id: project.id,
                type: DirectoryConstants.ARTIFACT_SCOPE_TYPE,
                name: project.name
            }
        };

        const favorite: Favorite = (yield effects.call([favoritesClient, favoritesClient.createFavorite], favoriteParameters)) as any;
        yield effects.put(
            FavoriteActions.addFavoriteSuccessful({
                id: favorite.id,
                artifactId: favorite.artifactId,
                artifactName: favorite.artifactName,
                artifactType: favorite.artifactType,
                artifactProperties: favorite.artifactProperties as {
                    [key: string]: string;
                },
                isDeleted: favorite.artifactIsDeleted
            })
        );
    } catch (error) {
        yield effects.put(FavoriteActions.addFavoriteFailed(artifactId, error));

        yield effects.call(traceException, pageContext, directoryContext.hubName, "Directory", error);
    }
}

/**
 * Saga which deletes a favorite
 * @param artifactId The artifact ID to delete
 * @param favoriteId The favorite ID to delete
 */
export function* deleteFavorite(artifactId: string, favoriteId: string, favoriteType: string): SagaIterator {
    yield effects.put(FavoriteActions.beginRemoveFavorite(artifactId));
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;
    try {
        const favoritesClient: IFavoriteRestClient = (yield effects.call([pageContext, pageContext.getRestClient], "IFavoriteRestClient")) as any;
        const project: IProjectData = (yield effects.select(currentProject)) as any;

        yield effects.call(
            [favoritesClient, favoritesClient.deleteFavoriteById],
            favoriteId,
            favoriteType,
            DirectoryConstants.ARTIFACT_SCOPE_TYPE,
            project.id
        );
        yield effects.put(FavoriteActions.removeFavoriteSuccessful(artifactId));
    } catch (error) {
        yield effects.put(FavoriteActions.removeFavoriteFailed(artifactId, error));

        yield effects.call(traceException, pageContext, directoryContext.hubName, "Directory", error);
    }
}

function getLocalStorageKey(areaName: string): string {
    return `${areaName}_${DirectoryConstants.LOCAL_STORAGE_KEY_PREFIX}`;
}
